import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import * as Icons from 'react-feather';
import DataTable from 'react-data-table-component';
import { Link } from 'react-router-dom';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { getStorageValues } from '../../../shared/CommonFunctions';
import SKAlerts, { CreateAlert } from '../../../shared/common-alerts/SKAlerts';
import { ReactComponent as SortArrow } from '../../../shared/img/chevron-up-down.svg';
import RichTextEditor from '../../../shared/rich-text/RichTextEditor';
import { isEmptyHtml, stripHtml } from '../../../shared/rich-text/richTextUtils';

const sortFeatureRows = (rows = []) =>
  [...rows].sort(
    (a, b) =>
      Number(a.DisplayOrder || 0) - Number(b.DisplayOrder || 0) ||
      Number(a.FeatureId || 0) - Number(b.FeatureId || 0),
  );

const getNextDisplayOrder = (rows = []) => {
  if (!rows.length) return 1;
  return Math.max(...rows.map((row) => Number(row.DisplayOrder) || 0)) + 1;
};

function ProductFeaturesTab({
  productId,
  features = [],
  readOnly = false,
  onRefresh,
  onOrderChange,
  featureOrderSaveToken = 0,
}) {
  const [alert, setAlert] = useState(null);
  const [featureRows, setFeatureRows] = useState(() => sortFeatureRows(features));
  const [orderDirty, setOrderDirty] = useState(false);
  const [selectedFeatureId, setSelectedFeatureId] = useState(null);
  const [draft, setDraft] = useState({ FeatureId: null, FeatureText: '' });
  const [editorSessionKey, setEditorSessionKey] = useState(0);

  useEffect(() => {
    if (featureOrderSaveToken > 0) {
      setOrderDirty(false);
    }
  }, [featureOrderSaveToken]);

  useEffect(() => {
    if (orderDirty) return;
    const sorted = sortFeatureRows(features);
    setFeatureRows(sorted);
    if (selectedFeatureId && !sorted.some((r) => r.FeatureId === selectedFeatureId)) {
      setSelectedFeatureId(null);
    }
  }, [features, orderDirty, selectedFeatureId]);

  useEffect(() => {
    if (!onOrderChange || !productId) return;
    onOrderChange({
      productId,
      orderList: featureRows.map((row, index) => ({
        FeatureId: row.FeatureId,
        DisplayOrder: index + 1,
      })),
      dirty: orderDirty,
    });
  }, [featureRows, orderDirty, productId, onOrderChange]);

  const resetDraft = () => {
    setDraft({ FeatureId: null, FeatureText: '' });
    setSelectedFeatureId(null);
    setEditorSessionKey((key) => key + 1);
  };

  const selectedIndex = useMemo(
    () => featureRows.findIndex((row) => row.FeatureId === selectedFeatureId),
    [featureRows, selectedFeatureId],
  );

  const canMoveUp = selectedIndex > 0;
  const canMoveDown = selectedIndex >= 0 && selectedIndex < featureRows.length - 1;

  const moveSelected = useCallback((direction) => {
    if (selectedIndex < 0) return;
    const target = direction === 'up' ? selectedIndex - 1 : selectedIndex + 1;
    if (target < 0 || target >= featureRows.length) return;

    setFeatureRows((prev) => {
      const updated = [...prev];
      [updated[selectedIndex], updated[target]] = [updated[target], updated[selectedIndex]];
      return updated.map((row, i) => ({ ...row, DisplayOrder: i + 1 }));
    });
    setOrderDirty(true);
  }, [selectedIndex, featureRows.length]);

  const saveFeature = async () => {
    if (!productId) {
      CreateAlert('Save product details first', 'Warning', setAlert);
      return;
    }
    if (isEmptyHtml(draft.FeatureText)) {
      CreateAlert('Feature text is required', 'Warning', setAlert);
      return;
    }

    let displayOrder = getNextDisplayOrder(featureRows);
    if (draft.FeatureId) {
      const existing = featureRows.find((row) => row.FeatureId === draft.FeatureId);
      displayOrder = existing?.DisplayOrder ?? displayOrder;
    }

    try {
      const response = await axios.post(`${getApiBaseUrl()}/ec-product/feature/save`, {
        FeatureId: draft.FeatureId,
        ProductId: productId,
        FeatureText: draft.FeatureText,
        DisplayOrder: displayOrder,
        UserId: getStorageValues('userId'),
      });
      const result = response.data?.data;
      if (result?.success === 1) {
        CreateAlert(result.message || 'Feature saved', 'Success', setAlert);
        resetDraft();
        if (onRefresh) onRefresh();
      } else {
        CreateAlert(result?.message || 'Failed to save feature', 'Error', setAlert);
      }
    } catch (err) {
      console.error(err);
      CreateAlert('Failed to save feature', 'Error', setAlert);
    }
  };

  const deleteFeature = useCallback(async (featureId) => {
    try {
      const response = await axios.post(`${getApiBaseUrl()}/ec-product/feature/delete`, {
        FeatureId: featureId,
        UserId: getStorageValues('userId'),
      });
      if (response.data?.data?.success === 1) {
        if (selectedFeatureId === featureId) {
          setSelectedFeatureId(null);
        }
        CreateAlert('Feature deleted', 'Success', setAlert);
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      console.error(err);
      CreateAlert('Failed to delete feature', 'Error', setAlert);
    }
  }, [onRefresh, selectedFeatureId]);

  const editRow = useCallback((row) => {
    setSelectedFeatureId(row.FeatureId);
    setDraft({
      FeatureId: row.FeatureId,
      FeatureText: row.FeatureText || '',
    });
    setEditorSessionKey((key) => key + 1);
    document.querySelector('.sk-product-feature-form')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);

  const conditionalRowStyles = useMemo(
    () => [
      {
        when: (row) => row.FeatureId === selectedFeatureId,
        style: {
          backgroundColor: 'rgba(18, 97, 160, 0.12)',
          borderLeft: '3px solid #1261A0',
        },
      },
    ],
    [selectedFeatureId],
  );

  const columns = useMemo(() => {
    const cols = [
      {
        name: (
          <div className="titlename">
            <span>Feature Text</span>
          </div>
        ),
        cell: (row) => {
          const preview = stripHtml(row.FeatureText);
          return <span title={preview}>{preview || '-'}</span>;
        },
        sortable: false,
        sortIcon: <SortArrow />,
      },
    ];

    if (!readOnly) {
      cols.push({
        name: <div className="titlename">Actions</div>,
        cell: (row) => (
          <div className="edit-delete-action d-flex align-items-center justify-content-end">
            <Link
              className="me-2 p-2"
              to=""
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                editRow(row);
              }}
            >
              <Icons.Edit className="feather-edit" />
            </Link>
            <Link
              className="confirm-text p-2"
              to=""
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                deleteFeature(row.FeatureId);
              }}
            >
              <Icons.Trash2 className="feather-trash-2" />
            </Link>
          </div>
        ),
        right: true,
        width: '120px',
        sortable: false,
      });
    }

    return cols;
  }, [readOnly, editRow, deleteFeature]);

  if (!productId) {
    return <div className="alert alert-info mb-0">Save the product details first to add features.</div>;
  }

  return (
    <>
      <SKAlerts alert={alert} />
      {!readOnly && (
        <div className="card mb-3 sk-product-feature-form">
          <div className="card-body">
            <h6 className="mb-2">{draft.FeatureId ? 'Edit Feature' : 'Add Feature'}</h6>
            <div className="row">
              <div className="col-lg-10">
                <RichTextEditor
                  loadKey={editorSessionKey}
                  value={draft.FeatureText}
                  onChange={(html) => setDraft((p) => ({ ...p, FeatureText: html }))}
                  placeholder="Enter product feature text"
                  readOnly={readOnly}
                />
              </div>
              <div className="col-lg-2 d-flex align-items-end gap-2">
                <button type="button" className="btn btn-submit" onClick={saveFeature}>
                  {draft.FeatureId ? 'Save' : 'Add'}
                </button>
                {draft.FeatureId ? (
                  <button type="button" className="btn btn-cancel" onClick={resetDraft}>Cancel</button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="d-flex align-items-stretch gap-2 sk-product-features-grid-row">
        <div className="flex-grow-1 min-width-0 card table-list-card sk-product-features-table">
          <div className="card-body">
            <DataTable
              className="table-responsive product-list"
              columns={columns}
              data={featureRows}
              keyField="FeatureId"
              pagination={false}
              fixedHeader
              fixedHeaderScrollHeight="320px"
              sortIcon={<SortArrow />}
              persistTableHead
              conditionalRowStyles={conditionalRowStyles}
              onRowClicked={(row) => setSelectedFeatureId(row.FeatureId)}
              pointerOnHover
              highlightOnHover
              noDataComponent={<span className="text-muted">No features added.</span>}
            />
          </div>
        </div>
        {!readOnly ? (
          <div className="sk-feature-order-controls d-flex flex-column justify-content-center gap-2">
            <button
              type="button"
              className="sk-order-btn btn btn-outline-primary"
              disabled={!canMoveUp}
              title="Move selected up"
              aria-label="Move selected up"
              onClick={() => moveSelected('up')}
            >
              <Icons.ChevronUp size={20} />
            </button>
            <button
              type="button"
              className="sk-order-btn btn btn-outline-primary"
              disabled={!canMoveDown}
              title="Move selected down"
              aria-label="Move selected down"
              onClick={() => moveSelected('down')}
            >
              <Icons.ChevronDown size={20} />
            </button>
          </div>
        ) : null}
      </div>
    </>
  );
}

export default ProductFeaturesTab;
